export interface SkillTreeNodeData {
    slug: string;
    title: string;
    image: string;
    requiresAuth: boolean;
    metaLabel?: string;
}

export interface SkillTreeTier {
    label: string;
    description: string;
    nodes: SkillTreeNodeData[];
}

export interface SkillTreeEdge {
    fromTier: number;
    fromIndex: number;
    toTier: number;
    toIndex: number;
}

export interface AcademyTrack {
    slug: string;
    title: string;
    tiers: SkillTreeTier[];
}

export const ethereumEvmTiers: SkillTreeTier[] = [
    {
        label: 'Foundation',
        description: 'Start here. Learn the language, then break your first contracts.',
        nodes: [
            { slug: 'ethereum-basics', title: 'Ethereum', image: 'ethereum-basics.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'solidity', title: 'Solidity', image: 'solidity.png', requiresAuth: false },
            { slug: 'introduction', title: 'Introduction', image: 'introduction.png', requiresAuth: false },
        ],
    },
    {
        label: 'Core Security',
        description: 'Your first real exploits. Find the flaws that drain wallets.',
        nodes: [
            { slug: 'access-control', title: 'Access Control', image: 'access-control.png', requiresAuth: false },
            { slug: 'txorigin', title: 'TX Origin', image: 'tx-origin.png', requiresAuth: true },
            { slug: 'randomness', title: 'Randomness', image: 'randomness.png', requiresAuth: true },
        ],
    },
    {
        label: 'Intermediate',
        description: 'The attacks that made headlines. Can you pull them off?',
        nodes: [
            { slug: 'reentrancy', title: 'Reentrancy', image: 'reentrancy.png', requiresAuth: true },
            { slug: 'denial-of-service', title: 'Denial of Service', image: 'denial-of-service.png', requiresAuth: true },
            { slug: 'signatures', title: 'Signatures', image: 'signatures.png', requiresAuth: true },
        ],
    },
    {
        label: 'Advanced',
        description: 'Proxies, storage slots, and gas wars. Only for the dedicated.',
        nodes: [
            { slug: 'upgradeability', title: 'Upgradeability', image: 'upgradeability.png', requiresAuth: true },
            { slug: 'gas-optimization', title: 'Gas Optimization', image: 'gas-optimization.png', requiresAuth: true },
        ],
    },
];

export const cantonDamlTiers: SkillTreeTier[] = [
    {
        label: 'Start Here',
        description: 'Start with DAML primitives, templates, choices, and ledgers.',
        nodes: [
            { slug: 'canton', title: 'Canton', image: 'canton.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'daml-foundation', title: 'Foundation', image: 'daml-foundation.png', requiresAuth: true },
            { slug: 'daml-advanced', title: 'Advanced', image: 'daml-advanced.png', requiresAuth: true },
        ],
    },
    {
        label: 'Core Security',
        description: 'Break authorization, availability, uniqueness, and state-integrity assumptions.',
        nodes: [
            { slug: 'daml-authorization', title: 'Authorization', image: 'daml-authorization.png', requiresAuth: true },
            { slug: 'daml-availability', title: 'Availability', image: 'daml-availability.png', requiresAuth: true },
            { slug: 'daml-keys-and-uniqueness', title: 'Keys & Uniqueness', image: 'daml-keys-and-uniqueness.png', requiresAuth: true },
            { slug: 'daml-state-integrity', title: 'State Integrity', image: 'daml-state-integrity.png', requiresAuth: true },
        ],
    },
    {
        label: 'Boundaries',
        description: 'Inspect off-ledger integration, privacy, and time-boundary failures.',
        nodes: [
            { slug: 'daml-offledger-integration', title: 'Off-Ledger Integration', image: 'daml-offledger-integration.png', requiresAuth: true },
            { slug: 'daml-privacy', title: 'Privacy', image: 'daml-privacy.png', requiresAuth: true },
            { slug: 'daml-time-and-boundaries', title: 'Time & Boundaries', image: 'daml-time-and-boundaries.png', requiresAuth: true },
        ],
    },
];

export const solanaAnchorTiers: SkillTreeTier[] = [
    {
        label: 'Orientation',
        description: 'Understand Solana accounts and then build the validation reflex.',
        nodes: [
            { slug: 'solana', title: 'Solana', image: 'solana-network-basics.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'solana-foundation', title: 'Foundation', image: 'solana-foundation.png', requiresAuth: true },
        ],
    },
    {
        label: 'Programs',
        description: 'Combine runtime invariants, then move the same ideas into Anchor.',
        nodes: [
            { slug: 'solana-advanced', title: 'Advanced', image: 'solana-advanced.png', requiresAuth: true },
            { slug: 'solana-anchor', title: 'Anchor', image: 'solana-anchor.png', requiresAuth: true },
        ],
    },
    {
        label: 'Solana Security',
        description: 'Exploit account validation, CPI, accounting, state, time, and availability bugs.',
        nodes: [
            { slug: 'solana-security-account-authority', title: 'Account & Authority', image: 'solana-security-account-authority.png', requiresAuth: true },
            { slug: 'solana-security-cpi-and-accounting', title: 'CPI & Accounting', image: 'solana-security-cpi-and-accounting.png', requiresAuth: true },
            { slug: 'solana-security-state-time-availability', title: 'State, Time & Availability', image: 'solana-security-state-time-availability.png', requiresAuth: true },
        ],
    },
    {
        label: 'Anchor Security',
        description: 'Break missing constraints, remaining-account flows, and lifecycle governance checks.',
        nodes: [
            { slug: 'solana-anchor-security-constraints', title: 'Constraints', image: 'solana-anchor-security-constraints.png', requiresAuth: true },
            { slug: 'solana-anchor-security-cpi-remaining-accounts', title: 'CPI & Remaining Accounts', image: 'solana-anchor-security-cpi-remaining-accounts.png', requiresAuth: true },
            { slug: 'solana-anchor-security-lifecycle-governance', title: 'Lifecycle & Governance', image: 'solana-anchor-security-lifecycle-governance.png', requiresAuth: true },
        ],
    },
];

export const bitcoinTiers: SkillTreeTier[] = [
    {
        label: 'Foundation',
        description: 'Start with proof of work, then keys, addresses, UTXOs, fees, and transaction bytes.',
        nodes: [
            { slug: 'bitcoin-basics', title: 'Bitcoin', image: 'bitcoin-network.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'bitcoin-foundation', title: 'Foundation', image: 'bitcoin-foundation.png', requiresAuth: true },
            { slug: 'bitcoin-transactions', title: 'Transactions', image: 'bitcoin-transactions.png', requiresAuth: true },
        ],
    },
    {
        label: 'Spending Rules',
        description: 'Build stack-based locking rules, hashlocks, multisig, witness scripts, and refunds.',
        nodes: [
            { slug: 'bitcoin-script', title: 'Script', image: 'bitcoin-script.png', requiresAuth: true },
            { slug: 'bitcoin-timelocks', title: 'Timelocks', image: 'bitcoin-timelocks.png', requiresAuth: true },
        ],
    },
    {
        label: 'Signing & Wallets',
        description: 'Inspect signature commitments, PSBT policy, and Taproot hashing boundaries.',
        nodes: [
            { slug: 'bitcoin-signatures', title: 'Signatures', image: 'bitcoin-signatures.png', requiresAuth: true },
            { slug: 'bitcoin-wallets', title: 'Wallets', image: 'bitcoin-wallet.png', requiresAuth: true },
            { slug: 'bitcoin-taproot', title: 'Taproot', image: 'bitcoin-taproot.png', requiresAuth: true },
        ],
    },
    {
        label: 'Security',
        description: 'Exploit bad randomness and wallet accounting failures that can destroy funds.',
        nodes: [
            { slug: 'bitcoin-security', title: 'Security', image: 'bitcoin-security.png', requiresAuth: true },
        ],
    },
];

export const starknetCairoTiers: SkillTreeTier[] = [
    {
        label: 'Foundation',
        description: 'Meet Starknet, then learn the Cairo language and map it onto Starknet contracts.',
        nodes: [
            { slug: 'starknet-basics', title: 'Starknet', image: 'starknet-network.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'cairo-foundation', title: 'Foundation', image: 'cairo-foundation.png', requiresAuth: true },
            { slug: 'cairo-starknet-contracts', title: 'Contracts', image: 'cairo-starknet-contracts.png', requiresAuth: true },
        ],
    },
    {
        label: 'Core Security',
        description: 'Your first Cairo exploits: break caller authorization and field-arithmetic accounting.',
        nodes: [
            { slug: 'cairo-security-access-control', title: 'Access Control', image: 'cairo-access-control.png', requiresAuth: true },
            { slug: 'cairo-security-accounting', title: 'Accounting', image: 'cairo-accounting.png', requiresAuth: true },
        ],
    },
    {
        label: 'State & Interactions',
        description: 'Abuse lifecycle and state assumptions, then untrusted cross-contract call paths.',
        nodes: [
            { slug: 'cairo-security-state-and-lifecycle', title: 'State & Lifecycle', image: 'cairo-state-lifecycle.png', requiresAuth: true },
            { slug: 'cairo-security-cross-contract', title: 'Cross Contract', image: 'cairo-cross-contract.png', requiresAuth: true },
        ],
    },
    {
        label: 'Advanced Security',
        description: 'Replay signatures, grief with denial of service, and hijack upgrades and deployment.',
        nodes: [
            { slug: 'cairo-security-signatures-and-accounts', title: 'Signatures & Accounts', image: 'cairo-signatures.png', requiresAuth: true },
            { slug: 'cairo-security-denial-of-service', title: 'Denial of Service', image: 'cairo-denial-of-service.png', requiresAuth: true },
            { slug: 'cairo-security-upgrades-and-deployment', title: 'Upgrades & Deployment', image: 'cairo-upgrades.png', requiresAuth: true },
        ],
    },
];

export const suiMoveTiers: SkillTreeTier[] = [
    {
        label: 'Foundation',
        description: 'Meet Sui, learn the Move language, model Sui objects, then rebuild them with real Sui primitives.',
        nodes: [
            { slug: 'sui-basics', title: 'Sui', image: 'sui-network.png', requiresAuth: false, metaLabel: 'Network Basics' },
            { slug: 'move-foundation', title: 'Foundation', image: 'move-foundation.png', requiresAuth: true },
            { slug: 'move-sui-objects', title: 'Objects', image: 'move-sui-objects.png', requiresAuth: true },
            { slug: 'move-sui-real-objects', title: 'Real Objects', image: 'move-sui-real-objects.png', requiresAuth: true },
        ],
    },
    {
        label: 'Core Security',
        description: 'Your first Move exploits: break capability authorization and asset accounting.',
        nodes: [
            { slug: 'move-sui-security-access-control', title: 'Access Control', image: 'move-sui-access-control.png', requiresAuth: true },
            { slug: 'move-sui-security-accounting', title: 'Accounting', image: 'move-sui-accounting.png', requiresAuth: true },
        ],
    },
    {
        label: 'Advanced Security',
        description: 'Abuse lifecycle and initialization, then break the object model itself.',
        nodes: [
            { slug: 'move-sui-security-state-lifecycle', title: 'State Lifecycle', image: 'move-sui-state-lifecycle.png', requiresAuth: true },
            { slug: 'move-sui-security-object-model', title: 'Object Model', image: 'move-sui-object-model.png', requiresAuth: true },
        ],
    },
];

export const academyTracks: AcademyTrack[] = [
    { slug: 'ethereum-evm', title: 'Ethereum & Solidity', tiers: ethereumEvmTiers },
    { slug: 'canton-daml', title: 'Canton & DAML', tiers: cantonDamlTiers },
    { slug: 'solana-and-anchor', title: 'Solana & Anchor', tiers: solanaAnchorTiers },
    { slug: 'bitcoin', title: 'Bitcoin', tiers: bitcoinTiers },
    { slug: 'starknet-cairo', title: 'Starknet & Cairo', tiers: starknetCairoTiers },
    // Hidden until challenges are added for this track
    // { slug: 'sui-move', title: 'Sui & Move', tiers: suiMoveTiers },
];

export const solanaChallengeCategoryTitles: Record<string, string> = {
    'solana-advanced': 'Solana Advanced',
    'solana-anchor': 'Solana Anchor',
    'solana-anchor-security-constraints': 'Solana Anchor Security: Constraints',
    'solana-anchor-security-cpi-remaining-accounts': 'Solana Anchor Security: CPI Remaining Accounts',
    'solana-anchor-security-lifecycle-governance': 'Solana Anchor Security: Lifecycle Governance',
    'solana-foundation': 'Solana Foundation',
    'solana-security-account-authority': 'Solana Security: Account Authority',
    'solana-security-cpi-and-accounting': 'Solana Security: CPI & Accounting',
    'solana-security-state-time-availability': 'Solana Security: State, Time & Availability',
};

export const solanaChallengeCategories = Object.keys(solanaChallengeCategoryTitles);

export const bitcoinChallengeCategoryTitles: Record<string, string> = {
    'bitcoin-foundation': 'Bitcoin Foundation',
    'bitcoin-transactions': 'Bitcoin Transactions',
    'bitcoin-script': 'Bitcoin Script',
    'bitcoin-timelocks': 'Bitcoin Timelocks',
    'bitcoin-signatures': 'Bitcoin Signatures',
    'bitcoin-wallets': 'Bitcoin Wallets',
    'bitcoin-taproot': 'Bitcoin Taproot',
    'bitcoin-security': 'Bitcoin Security',
};

export const bitcoinChallengeCategories = Object.keys(bitcoinChallengeCategoryTitles);

export const cairoChallengeCategoryTitles: Record<string, string> = {
    'cairo-foundation': 'Cairo Foundation',
    'cairo-starknet-contracts': 'Starknet Contracts',
    'cairo-security-access-control': 'Cairo Security: Access Control',
    'cairo-security-accounting': 'Cairo Security: Accounting',
    'cairo-security-state-and-lifecycle': 'Cairo Security: State & Lifecycle',
    'cairo-security-cross-contract': 'Cairo Security: Cross Contract',
    'cairo-security-signatures-and-accounts': 'Cairo Security: Signatures & Accounts',
    'cairo-security-denial-of-service': 'Cairo Security: Denial of Service',
    'cairo-security-upgrades-and-deployment': 'Cairo Security: Upgrades & Deployment',
};

export const cairoChallengeCategories = Object.keys(cairoChallengeCategoryTitles);

export const moveSuiChallengeCategoryTitles: Record<string, string> = {
    'move-foundation': 'Move Foundation',
    'move-sui-objects': 'Sui Objects',
    'move-sui-real-objects': 'Sui Real Objects',
    'move-sui-security-access-control': 'Sui Security: Access Control',
    'move-sui-security-accounting': 'Sui Security: Accounting',
    'move-sui-security-state-lifecycle': 'Sui Security: State Lifecycle',
    'move-sui-security-object-model': 'Sui Security: Object Model',
};

export const moveSuiChallengeCategories = Object.keys(moveSuiChallengeCategoryTitles);

export const categoryTitles: Record<string, string> = {
    ...academyTracks.reduce((titles, track) => {
        track.tiers.forEach((tier) => {
            tier.nodes.forEach((node) => {
                titles[node.slug] = node.title;
            });
        });
        return titles;
    }, {} as Record<string, string>),
    ...solanaChallengeCategoryTitles,
    ...bitcoinChallengeCategoryTitles,
    ...cairoChallengeCategoryTitles,
    ...moveSuiChallengeCategoryTitles,
};

export const academyCategories = academyTracks.flatMap((track) =>
    track.tiers.flatMap((tier) => tier.nodes.map((node) => node.slug))
);

export const challengeCategories = [
    ...Array.from(new Set([
        ...academyCategories,
        ...solanaChallengeCategories,
        ...bitcoinChallengeCategories,
        ...cairoChallengeCategories,
        ...moveSuiChallengeCategories,
    ])),
    'missions',
    'season1',
];

export const getAcademyTrack = (trackSlug: string | undefined) => {
    return academyTracks.find((track) => track.slug === trackSlug) || academyTracks[0];
};

export const getAcademyTrackForCategory = (category: string | undefined) => {
    return academyTracks.find((track) =>
        track.tiers.some((tier) => tier.nodes.some((node) => node.slug === category))
    ) || academyTracks[0];
};

export const edges: SkillTreeEdge[] = [
    // Tier 0 → Tier 1 (2 fan out to 3)
    { fromTier: 0, fromIndex: 0, toTier: 1, toIndex: 0 },
    { fromTier: 0, fromIndex: 1, toTier: 1, toIndex: 0 },
    { fromTier: 0, fromIndex: 1, toTier: 1, toIndex: 1 },
    { fromTier: 0, fromIndex: 1, toTier: 1, toIndex: 2 },
    // Tier 1 → Tier 2 (1:1 mapping)
    { fromTier: 1, fromIndex: 0, toTier: 2, toIndex: 0 },
    { fromTier: 1, fromIndex: 1, toTier: 2, toIndex: 1 },
    { fromTier: 1, fromIndex: 2, toTier: 2, toIndex: 2 },
    // Tier 2 → Tier 3 (3 converge to 2)
    { fromTier: 2, fromIndex: 0, toTier: 3, toIndex: 0 },
    { fromTier: 2, fromIndex: 1, toTier: 3, toIndex: 0 },
    { fromTier: 2, fromIndex: 1, toTier: 3, toIndex: 1 },
    { fromTier: 2, fromIndex: 2, toTier: 3, toIndex: 1 },
];
